import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { RelatedAsset } from '@app/core/model/entities/asset/asset';
import { isAssetRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import { IRelatedSpace, RelatedSpace, Space } from '@app/core/model/entities/asset/space';
import { SpacesService } from '@app/features/main/views/organization-spaces/spaces.service';
import { AbstractFieldBuilder, FieldStateMode } from '@app/shared/components/fields/abstract.field';
import { eq } from '@app/shared/extra/filter-model-builder';
import { getLocale } from '@app/shared/extra/utils';
import { ColumnBuilder } from '@app/shared/grid/column-builder';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import { GridOptionsService } from '@app/shared/grid/grid-options.service';
import { GridStateService } from '@app/shared/services/grid-state.service';
import { GridApi, GridOptions, GridReadyEvent, RowSelectedEvent } from 'ag-grid-community';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, EMPTY, exhaustMap, merge, Observable, of, pipe, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'spaces-field-builder',
  templateUrl: './spaces-field-builder.component.html',
  styleUrls: ['./spaces-field-builder.component.scss'],
  standalone: false
})
export class SpacesFieldBuilderComponent extends AbstractFieldBuilder<FormControl<(RelatedSpace | Space)[]>> implements OnInit, AfterViewInit {
  private readonly deviceDetectorService = inject(DeviceDetectorService);
  private readonly spacesService = inject(SpacesService);
  private readonly columnBuilder = inject(ColumnBuilder);
  private readonly gridStateService = inject(GridStateService);
  private readonly gridOptionsService = inject(GridOptionsService);

  public noAsset = true;
  public selectedSpaces: (RelatedSpace | Space)[] = [];
  public initialSpaces: RelatedSpace[];
  public widthAuto = true;
  public gridApi: GridApi<Space>;
  public gridOptions: GridOptions<Space>;
  protected invalidRows: string[] = [];
  protected spaces = new ReplaySubject<Space[]>(1);
  protected assetTypeAllowsSpaceModule = false;
  protected assetChanged$ = new BehaviorSubject<string>(null);

  /**
   * Key is space's id and value is space's display name
   * If there is no space, key is null and string is this.appConfig.EMPTY_FIELD_VALUE
   */
  public spacesMap = new Map<string, string>();

  @ViewChild('gridContainer') public gridContainer: ElementRef;

  protected cancel$ = new Subject<void>();

  constructor() {
    super();
    this.selectedSpaces = this.fieldInitValue ?? [];

    // Add additional options to the gridOption
    const extraSpaceGridOptions = <GridOptions>{
      ...this.gridOptionsService.staticSelectSpacesGridOptions,
      suppressContextMenu: false,
      multiSortKey: this.appConfig.GRID_MULTI_SORT_KEY,
      domLayout: 'autoHeight',
      getRowId: (params) => params.data.id,
      animateRows: true,
      overlayLoadingTemplate:
        '<span class="ag-overlay-loading-center">' + this.translate.instant('LABEL.LOADING') + '</span>',
      localeText: this.columnBuilder.localeTextCommon(),
      rowClassRules: {
        'invalid-row': (params): boolean => this.invalidRows.includes(params.data.id)
      },
      defaultColDef: {
        resizable: true,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        filter: true
      },
      treeData: true,
      getMainMenuItems: (params) => params.defaultItems,
      getRowHeight: () => this.gridOptionsService.staticGridOptions.rowHeight,
      onRowClicked: this.onRowSelected.bind(this),
      onRowSelected: this.onRowSelected.bind(this),
      onGridSizeChanged: () => this.gridApi.sizeColumnsToFit()
    };
    this.gridOptions = {
      ...this.gridOptionsService.staticGridOptions,
      ...extraSpaceGridOptions
    };
  }

  public ngOnInit(): void {
    this.initialSpaces = this.fieldInitValue?.map((space: IRelatedSpace) => new RelatedSpace(space)) ?? [];
    this.form = new FormGroup<{ field: FormControl<(RelatedSpace | Space)[]> }>({
      field: new FormControl<RelatedSpace[]>(
        this.initialSpaces,
        this.computeValidators(),
        this.computeAsyncValidators()
      )
    });

    // Reusable pipeline
    const reloadNewPage = pipe(
      filter(() => this.currentMode === FieldStateMode.EDIT || this.isCreatingEntity),
      tap(() => this.gridApi?.setGridOption('loading', true)),
      // Ignore all new inputs until the api call completes
      exhaustMap(() => {
        if (this.assetChanged$.value && this.assetTypeAllowsSpaceModule) {
          this.form.controls.field.addValidators(Validators.required);
          this.form.controls.field.updateValueAndValidity();
          return this.loadAllSpaces(this.assetChanged$.value);
        } else {
          this.form.controls.field.removeValidators(Validators.required);
          this.form.controls.field.updateValueAndValidity();
          this.selectedSpaces = [];
          this.form.controls.field.setValue(this.selectedSpaces);
          return of<Space[]>([]);
        }
      }),
      // Emit the accumulation of existing data and the new page
      tap(entitiesNewPage => this.spaces.next(entitiesNewPage))
    );

    merge(
      this.assetChanged$.pipe(
        reloadNewPage
      ),
      this.getFieldGroupState().pipe(
        filter(newMode => newMode === FieldStateMode.EDIT),
        debounceTime(500),
        reloadNewPage
      )
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    // this.viewToggled and this.editToggled are initialised but never re-written
    // Use their value to initialise a local variable which can change over time
    this.assetTypeAllowsSpaceModule = this.viewToggled && this.editToggled;

    // FIXME TTT-4797 SSRM on space tree data
    this.formStateService.loaded$
      .pipe(
        switchMap(() => {
          // Load spaces
          if (this.getFormChanges(['asset'])) {
            // If the field group contains an Asset field whose value can change, load Spaces whenever a new Asset is selected
            return this.getFormChanges(['asset'])
              .pipe(
                // Load spaces if the asset module allows it
                tap((changes) => {
                  const asset: RelatedAsset = changes.value;
                  this.assetTypeAllowsSpaceModule = asset?.typeModules?.includes(EntityTypeEnum.SPACE);

                  this.noAsset = !asset?.id;
                  this.assetChanged$.next(asset?.id);
                })
              );
          } else {
            // Form does not contain an Asset field
            this.noAsset = false;

            // Space entity is related to an asset
            // Either the asset is on the current entity stack or is obtained through the relatedAssets array
            if (this.assetTypeAllowsSpaceModule) {
              if (!!this.appManager.currentAsset) {
                this.assetChanged$.next(this.appManager.currentAsset.id);
              } else if (!!this.appManager.currentEquipment?.assetId) {
                this.assetChanged$.next(this.appManager.currentEquipment.assetId);
              } else if (isAssetRelatedEntity(this.entity) && this.entity.relatedAssets.length > 0) {
                this.assetChanged$.next(this.entity.relatedAssets[0].id);
              }
            }

            return EMPTY;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();

    this.setSelectedEntities();
  }

  /**
   * // FIXME Add pagination TTT-4797
   * Load all spaces related to an asset by building corresponding select params
   * @param assetId The asset's Id
   * @returns The list of spaces
   */
  private loadAllSpaces(assetId: string): Observable<Space[]> {
    const selectParams: SelectParams = {
      locale: getLocale(),
      filters: [eq('assetId', assetId)]
    };

    return this.spacesService.loadAllSpaces(selectParams);
  }

  /**
   * This method will set selectable entities in the grid.
   * @param spaces A list of Space entities
   */
  protected setGridData(spaces: Space[]): void {
    this.gridApi.setGridOption('rowData', spaces);
  }

  /**
   * This method will set current selected entities.
   */
  protected setSelectedEntities(): void {
    this.selectedSpaces = this.initialSpaces;
    this.setFieldValue(this.selectedSpaces?.map(space => space.id));
    this.setFieldInitialValue(this.selectedSpaces?.map(space => space.id));
    this.getNextState();
    this.selectedSpaces?.forEach(space => this.spacesMap.set(space.id, space.toString()));
    if (this.spacesMap.size === 0) {
      this.spacesMap.set(null, this.appConfig.EMPTY_FIELD_VALUE);
    }
  }

  public ngAfterViewInit(): void {
    this.setupHooks();
    this.form.controls.field.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(50),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((spaces) => this.setFieldValue(spaces?.map(space => space.id)));
  }

  /**
   * Event thrown by the grid once it is loaded
   * @param params
   */
  public onGridReady(params: GridReadyEvent): void {
    this.invalidRows = [];
    this.gridApi = params.api;
    this.gridApi.applyColumnState({state: [{colId: 'ag-Grid-AutoColumn', sort: 'asc'}]});
    if (this.deviceDetectorService.isMobile()) {
      this.gridApi.applyColumnState({
        state: [
          {colId: 'ag-Grid-AutoColumn'},
          {colId: 'identifier'}
        ]
      });
    }
    this.spaces.pipe(takeUntil(this.cancel$), takeUntilDestroyed(this.destroyRef))
      .subscribe(spaces => {
        this.setGridData(spaces);
        this.gridApi.sizeColumnsToFit();
        this.selectedSpaces.forEach(space => {
          const rowNode = this.gridApi.getRowNode(space.id);
          if (rowNode?.data?.parentPath) {
            rowNode.data.parentPath.forEach((parentId: string) => {
              this.gridApi.getRowNode(parentId).setExpanded(true);
            });
          }
          rowNode?.setSelected(true);
        });
        this.gridApi.setGridOption('loading', false);
        if (!spaces.length) this.gridApi.showNoRowsOverlay();
      });

    // Focus and scroll to field
    this.gridContainer.nativeElement.focus();
    this.gridContainer.nativeElement.scrollIntoView({behavior: 'instant', block: 'center'});
  }

  public onClickSave(): void {
    if (this.gridApi.getSelectedRows().length > 0) {
      super.onClickSave();
    } else {
      this.cancel();
    }
  }

  /**
   * Event thrown by the grid when a row is selected or deselected.
   * @param event RowSelectedEvent
   */
  public onRowSelected(event: RowSelectedEvent): void {
    this.selectedSpaces = this.gridApi.getSelectedRows().map(space => space.toRelatedSpace());
    this.form.controls.field.setValue(this.selectedSpaces);
    this.form.controls.field.markAsDirty();

    const nodesToRefresh = [event.node];

    // Check if row is invalid and update invalid rows
    if (event.node.isSelected() && (this.hasParentSelected(event.node.data) || this.hasChildrenSelected(event.node.data))) {
      this.invalidRows.push(event.node.data.id);
    } else if (!event.node.isSelected()) {
      this.invalidRows = this.invalidRows.filter(row => {
        const rowNode = this.gridApi.getRowNode(row);
        nodesToRefresh.push(rowNode);
        return row !== event.node.data.id
          && (this.hasParentSelected(rowNode.data) || this.hasChildrenSelected(rowNode.data));
      });
    }

    // Refresh rows
    this.gridApi.redrawRows({rowNodes: nodesToRefresh});
  }

  /**
   * Restore field's value to original Spaces.
   */
  public cancel(): void {
    super.cancel();
    this.selectedSpaces = this.initialSpaces;
    this.cancel$.next();
  }

  /**
   * Checks if a Space has ancestors that are selected in the grid.
   * @param space Space to check.
   * @return true if at least one ancestor is selected, false otherwise.
   * @private
   */
  private hasParentSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows()
      .some(selectedRow => space.parentPath.includes(selectedRow.id));
  }

  /**
   * Checks if a Space has children that are selected in the grid.
   * @param space Space to check.
   * @return true is at least one child is selected, false otherwise.
   * @private
   */
  private hasChildrenSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows()
      .some(selectedRow => selectedRow.parentPath.includes(space.id));
  }

  /**
   * Navigate to the Space sheet of the related Asset
   */
  public async navigateToAssetSpaceSheet(): Promise<void> {
    if (this.assetTypeAllowsSpaceModule) return;

    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.FIELD_CLICK,
      NavigateToEnum.SHEET,
      EntityTypeEnum.ASSET,
      this.entity['assetId']
    );
    await this.spacesService.navigateToAssetSheet(this.entity['assetId']);
  }

  /**
   * Open Space sheet of the related Asset and specify which row to select in datagrid
   * @param spaceId
   */
  public onSpaceFieldClick(spaceId: string): void {
    if (spaceId == null) return;

    this.navigateToAssetSpaceSheet().then(() => {
      this.gridStateService.setRowToSelect(spaceId);
    });
  }
}
